import { useState } from 'react';
import { Command } from 'cmdk';
import * as RD from '@radix-ui/react-dialog';
import { ArrowRight, Search, User, Wallet, X } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useSearch } from '@/api/queries';
import { useChain } from '@/chain/ChainProvider';
import { fmtUsd, shortAddr } from '@/lib/format';
import { useDebounced } from '@/lib/useDebounced';
import { looksLikeAddress } from '@/lib/validate';
import { useUi } from '@/store/ui';
import { CoinAvatar, Delta, WalletAvatar } from '@/ui/bits';

export function SearchPalette() {
  const open = useUi((s) => s.palette);
  const set = useUi((s) => s.set);
  const [q, setQ] = useState('');
  // One request per pause in typing, not per keystroke (the api bucket is 300 / min per IP).
  const search = useSearch(useDebounced(q, 250));
  const navigate = useNavigate();
  const { config } = useChain();
  const go = (to: string) => {
    set({ palette: false });
    setQ('');
    navigate(to);
  };
  const addr = looksLikeAddress(q) ? q.trim() : null;
  const coins = search.data?.coins ?? [];
  const wallets = search.data?.wallets ?? [];
  return (
    <RD.Root open={open} onOpenChange={(o) => set({ palette: o })}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-40 animate-fade-in bg-black/60" />
        <RD.Content className="fixed top-[12vh] left-1/2 z-50 w-[min(600px,calc(100vw-20px))] -translate-x-1/2 animate-pop-in overflow-hidden rounded-xl border border-line-2 bg-panel shadow-2xl shadow-black/60" aria-describedby={undefined}>
          <RD.Title className="sr-only">Search coins and wallets</RD.Title>
          <Command shouldFilter={false} loop>
            <div className="flex items-center gap-2 border-b border-line px-3">
              <Search className="size-4 text-fg-3" />
              <Command.Input value={q} onValueChange={setQ} placeholder="Name, ticker or address" className="h-12 min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-4" autoFocus />
              <RD.Close className="rounded-full p-2 text-fg-3 hover:text-accent" aria-label="Close search"><X size={15} /></RD.Close>
            </div>
            <Command.List className="max-h-[60vh] overflow-y-auto p-1.5">
              {!q && <div className="px-3 py-6 text-center text-xs text-fg-4">Find a coin or wallet.</div>}
              {q && !search.isFetching && coins.length === 0 && wallets.length === 0 && !addr && <Command.Empty className="px-3 py-6 text-center text-xs text-fg-3">Nothing on Raydius matches “{q}”.</Command.Empty>}
              {addr && (
                <Command.Group heading="Address" className="text-2xs text-fg-4 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5">
                  <Command.Item value={`open-coin-${addr}`} onSelect={() => go(`/coin/${addr}`)} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-fg-2 data-[selected=true]:bg-hover data-[selected=true]:text-fg">
                    <ArrowRight className="size-4 text-accent" /> Open coin <span className="num text-fg-3">{shortAddr(addr, 6, 6)}</span>
                  </Command.Item>
                  <Command.Item value={`open-wallet-${addr}`} onSelect={() => go(`/profile/${addr}`)} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-fg-2 data-[selected=true]:bg-hover data-[selected=true]:text-fg">
                    <Wallet className="size-4 text-accent" /> Open wallet <span className="num text-fg-3">{shortAddr(addr, 6, 6)}</span>
                  </Command.Item>
                </Command.Group>
              )}
              {coins.length > 0 && (
                <Command.Group heading="Coins" className="text-2xs text-fg-4 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5">
                  {coins.map((c) => (
                    <Command.Item key={c.mint} value={c.mint} onSelect={() => go(`/coin/${c.mint}`)} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 data-[selected=true]:bg-hover">
                      <CoinAvatar src={c.image} symbol={c.symbol} seed={c.mint} size={30} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-fg">
                          {c.symbol} <span className="font-normal text-fg-3">{c.name}</span>
                        </div>
                        <div className="num text-2xs text-fg-4">{shortAddr(c.mint, 5, 5)}</div>
                      </div>
                      <div className="text-right">
                        <div className="num text-sm text-fg">{fmtUsd(c.marketCapSol * config.solUsd)}</div>
                        <Delta value={c.changePct.h24} className="text-2xs" />
                      </div>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
              {wallets.length > 0 && (
                <Command.Group heading="Wallets" className="text-2xs text-fg-4 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5">
                  {wallets.map((w) => (
                    <Command.Item key={w.address} value={`w-${w.address}`} onSelect={() => go(`/profile/${w.address}`)} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 data-[selected=true]:bg-hover">
                      <WalletAvatar address={w.address} size={26} />
                      <span className="num flex-1 text-sm text-fg">{shortAddr(w.address, 6, 6)}</span>
                      <span className="text-2xs text-fg-3">
                        <User className="mr-1 inline size-3" />
                        {w.created} created · {w.trades} trades
                      </span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
            </Command.List>
          </Command>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

